<script setup lang="ts">
import 'swiper/css';
import { register } from 'swiper/element/bundle';

const Swiper = defineAsyncComponent(() =>
  import('swiper/vue').then((m) => m.Swiper),
);

const SwiperSlide = defineAsyncComponent(async () => {
  const m = await import('swiper/vue');

  return m.SwiperSlide;
});

onBeforeMount(() => {
  register();
});

type Props = {
  banners?: AppearanceBanner[];
  rounded?: boolean;
  localImages?: boolean;
};

const {
  banners = [],
  rounded = false,
  localImages = false,
} = defineProps<Props>();

const isBannerVisible = computed<boolean>(() => {
  return banners.length > 0;
});

function filterActionBanner(banner: (typeof banners)[number]) {
  if (banner.action) {
    if (banner.action_type === 'route') {
      const actionData = banner.action.replace(/https?:\/\/[^/]+/gi, '');

      return actionData;
    } else {
      return banner.action;
    }
  }

  return '/';
}
</script>

<template>
  <div
    v-if="isBannerVisible"
    class="bannerClass"
  >
    <div
      class="bannerWrapper group"
      :class="[{ rounded: rounded }]"
    >
      <div
        class="sl_navprev absolute left-5 lg:left-10 top-[50%] translate-y-[-50%] translate-x-[-50%] z-10 text-white items-center justify-center flex text-lg lg:text-3xl hover:opacity-75 p-5"
      >
        <NuxtIcon
          class="w-6 h-6 sm:w-9 sm:h-9 flex items-center justify-center rounded-[30px] border border-[#00c3ff] bg-[#030a11]"
          name="arrows/angle_left"
        />
      </div>

      <div
        class="sl_navnext absolute right-5 lg:right-10 top-[50%] translate-y-[-50%] translate-x-[50%] z-10 text-white items-center justify-center flex text-lg lg:text-3xl hover:opacity-75 p-5"
      >
        <NuxtIcon
          class="w-6 h-6 sm:w-9 sm:h-9 flex items-center justify-center rounded-[30px] border border-[#00c3ff] bg-[#030a11]"
          name="arrows/angle_right"
        />
      </div>

      <div
        class="sl_paginav absolute !top-auto !bottom-3 lg:!bottom-6 !left-[50%] translate-x-[-50%] z-10 py-0 px-8 flex justify-center items-center gap-1 lg:gap-3 !w-full"
      ></div>

      <Swiper
        :auto-height="true"
        :autoplay="{
          delay: 7000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }"
        :loop="true"
        :navigation="{
          nextEl: '.sl_navnext',
          prevEl: '.sl_navprev',
        }"
        :pagination="{
          el: '.sl_paginav',
          clickable: true,
          type: 'bullets',
          bulletElement: 'span',
          bulletClass: 'flex-1 h-1',
          bulletActiveClass: 'bg-white',
        }"
        :slides-per-view="1"
        :space-between="0"
        :speed="1"
      >
        <SwiperSlide
          v-for="(banner, index) in banners"
          :key="index"
          class="px-5 pt-3 sm:px-0 sm:pt-0"
        >
          <NuxtLink
            :external="banner.action_type === 'external_link'"
            no-prefetch
            :target="
              banner.action_type === 'external_link' ? '_blank' : '_self'
            "
            :to="filterActionBanner(banner)"
          >
            <img
              :alt="`Banner ${index}`"
              class="w-full rounded-lg"
              :class="banner?.action ? 'cursor-pointer' : ''"
              :src="
                localImages
                  ? getAssetsUrlForCDN(banner?.image)
                  : getUrlForApiImage(banner?.image)
              "
            />
          </NuxtLink>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bannerClass {
  @apply select-none -mt-4 -mx-5 mb-3.5 sm:m-0 sm:mb-4 lg:mb-6 min-h-32;

  .bannerWrapper {
    @apply relative sm:rounded overflow-hidden;
  }
}
</style>
